import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import { useIxmUserauthV1SessionServiceForgotPassword } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-session-service-forgot-password';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import { Typography } from '@interxfi/design-system/components/typography';
import { AppTextInputWithLens } from '../../components/form/app-text-input';
import { AuthLayout } from '../../features/auth/layout';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../util/error-handler';
import { getPageHead } from '../../util/routing/meta';

export const Route = createFileRoute('/_auth/forgot-password')({
  head: () => getPageHead('Forgot Password'),
  component: ForgotPassword,
});

const forgotPasswordFormSchema = z.object({
  email: z.email({ error: 'Email must be a valid email' }).nonempty({ error: 'Email is required' }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordFormSchema>;

const defaultValues: ForgotPasswordFormValues = {
  email: '',
};

const errorMessageGetter = buildDefaultErrorMessageGetter('Forgot Password request failed', 'start the forgot password flow');

function ForgotPassword() {
  const addToast = useAddToast();
  const { mutateAsync, isPending, error } = useIxmUserauthV1SessionServiceForgotPassword();
  useErrorHandler(error, errorMessageGetter);

  const [buttonState, buttonRef] = useButtonState(isPending, error);
  const form = useForm<ForgotPasswordFormValues>({
    defaultValues,
    mode: 'all',
    resolver: zodResolver(forgotPasswordFormSchema),
  });

  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset,
  } = form;

  const lens = useLens({ control });

  async function onSubmit(value: ForgotPasswordFormValues) {
    // TODO: add captcha
    try {
      await mutateAsync(value);

      addToast({
        id: 'forgotPasswordSuccess',
        content: `An email was sent to ${value.email} if an account exists for that address.`,
        variant: 'success',
      });

      reset({ ...defaultValues }, { keepDefaultValues: true, keepDirty: false, keepTouched: false, keepErrors: false });
    } catch {}
  }

  return (
    <FormProvider {...form}>
      <AuthLayout>
        <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading">
          Forgot Password
        </Typography>

        <Typography lineHeight="1.125rem" size="0.875rem">
          Get a link to reset your password. (If you don&apos;t receive an email, please check your spam folder or try again later.)
        </Typography>

        <VerticalStack as="form" gap={4} onSubmit={handleSubmit(onSubmit)}>
          <AppTextInputWithLens label="Email" lens={lens.focus('email')} required />

          <Button disabled={!isValid} fullWidth ref={buttonRef} size="large" state={buttonState} type="submit">
            Send Link
          </Button>
        </VerticalStack>

        <VerticalStack inlineAlign="center">
          <TextLink LinkElement={Link} size="medium" to="/login" underline="hover">
            Return to Sign In
          </TextLink>
        </VerticalStack>
      </AuthLayout>
    </FormProvider>
  );
}
